/**
 * Descriptor de Condomínio. Campos de `CondominioOut` (a administradora é uma
 * referência a Empresa), mais as correlações em abas (o mesmo padrão de Pessoa):
 * contatos, endereços, papéis (síndico/morador…), documentos, núcleo econômico, e dossiê + grafo.
 */
import { Home } from 'lucide-react';
import { createElement } from 'react';
import { PainelImoveisCondominio } from '@/components/crud/paineis/PainelImoveisCondominio';
import { PainelNucleo } from '@/components/crud/paineis/PainelNucleo';
import type { CondominioCad } from '@/lib/api';
import { definirRecurso } from '@/lib/recursos/tipos';

const TIPO = [
  { valor: 'residencial', rotulo: 'Residencial' },
  { valor: 'comercial', rotulo: 'Comercial' },
  { valor: 'misto', rotulo: 'Misto' },
];

export const condominio = definirRecurso<CondominioCad>({
  chave: 'condominios',
  endpoint: '/cadastro/condominios',
  ownerTipo: 'condominio',
  titulo: 'Condomínios',
  tituloSingular: 'condomínio',
  descricao: 'Condomínios, sua administradora e os papéis (síndico com histórico).',
  icone: Home,
  capacidades: { podeEditar: true, podeExcluir: true },
  buscaPlaceholder: 'Buscar por nome ou CNPJ',
  rotuloItem: (c) => c.nome,

  colunas: [
    { chave: 'nome', rotulo: 'Nome' },
    { chave: 'tipo', rotulo: 'Tipo', ocultarEm: 'sm' },
    { chave: 'cnpj', rotulo: 'CNPJ', formato: 'cnpj', mono: true },
  ],

  camposCriar: [
    { chave: 'nome', rotulo: 'Nome', tipo: 'texto', obrigatorio: true },
    { chave: 'cnpj', rotulo: 'CNPJ', tipo: 'documento', formato: 'cnpj' },
  ],

  importarPlanilha: {
    endpoint: '/cadastro/ingestao/condominios',
    descricao:
      'Suba um Excel/CSV de condomínios. A IA sugere o mapa coluna→campo; havendo administradora ' +
      '(nome/CNPJ), a empresa é buscada no banco (ou criada) e vinculada. Você revisa antes de importar.',
  },

  gruposEdicao: [
    {
      id: 'identificacao',
      titulo: 'Identificação',
      campos: [
        { chave: 'nome', rotulo: 'Nome', tipo: 'texto', obrigatorio: true, colSpan: 2 },
        { chave: 'cnpj', rotulo: 'CNPJ', tipo: 'documento', formato: 'cnpj' },
        { chave: 'tipo', rotulo: 'Tipo', tipo: 'select', opcoes: TIPO },
        {
          chave: 'administradora_id',
          rotulo: 'Administradora',
          tipo: 'referencia',
          refEndpoint: '/cadastro/empresas',
          refRotulo: 'razao_social',
        },
        { chave: 'total_unidades', rotulo: 'Total de unidades', tipo: 'numero' },
        { chave: 'data_instituicao', rotulo: 'Data de instituição', tipo: 'data' },
      ],
    },
    {
      id: 'sistema',
      titulo: 'Situação e observações',
      campos: [
        { chave: 'ativo', rotulo: 'Ativo', tipo: 'booleano' },
        { chave: 'observacoes', rotulo: 'Observações', tipo: 'texto-longo', colSpan: 2 },
        { chave: 'created_at', rotulo: 'Criado em', tipo: 'leitura' },
        { chave: 'updated_at', rotulo: 'Atualizado em', tipo: 'leitura' },
      ],
    },
  ],

  paineis: [
    { tipo: 'contatos' },
    { tipo: 'enderecos' },
    { tipo: 'papeis' },
    { tipo: 'documentos' },
    { tipo: 'dossie' },
  ],

  abas: [
    {
      id: 'dados',
      rotulo: 'Dados do Condomínio',
      grupos: ['identificacao', 'sistema'],
      paineis: [{ tipo: 'tags' }],
    },
    { id: 'contatos', rotulo: 'Contatos', paineis: [{ tipo: 'contatos' }] },
    { id: 'enderecos', rotulo: 'Endereços', paineis: [{ tipo: 'enderecos' }] },
    { id: 'papeis', rotulo: 'Síndico e Moradores', paineis: [{ tipo: 'papeis' }] },
    {
      id: 'imoveis',
      rotulo: 'Imóveis',
      paineis: [
        {
          tipo: 'custom',
          id: 'imoveis-condominio',
          titulo: 'Imóveis',
          render: ({ item }) => createElement(PainelImoveisCondominio, { condominioId: item.id }),
        },
      ],
    },
    { id: 'documentos', rotulo: 'Documentos', paineis: [{ tipo: 'documentos' }] },
    {
      id: 'nucleo',
      rotulo: 'Núcleo',
      paineis: [
        {
          tipo: 'custom',
          id: 'nucleo-economico',
          titulo: 'Núcleo econômico',
          render: ({ item }) =>
            createElement(PainelNucleo, { ownerTipo: 'condominio', ownerId: item.id }),
        },
      ],
    },
    { id: 'dossie', rotulo: 'Dossiê', paineis: [{ tipo: 'dossie' }] },
    { id: 'grafo', rotulo: 'Grafo', paineis: [{ tipo: 'grafo' }] },
  ],
});
